// Colour derivations from the variable registry, in plain JS.
//
// The MapLibre-free half of the palette work: evaluating the ramp at a value,
// the legend gradient, the band scale. It lives in
// lib/ because it is pure and because the city-page renderer (city-view.ts) needs
// the same ramp the map uses — a chart bar and the field pixel under it must be
// the same colour. The MapLibre `interpolate`/`step` expression builders stay in
// src/client/palettes.ts, which re-exports everything here so call sites keep one
// import for "the variables and their colours".

import { VAR } from './variables.ts';
import type { VarKey } from './variables.ts';

// The registry itself: entries carry label/unit/stops.
export const PALETTES = VAR;

// The gradient colour at a given value, evaluated in JS — the same linear
// interpolation MapLibre does on the map, so a legend swatch matches the field.
// Used to tint the plain-language band labels.
export function colorAt(varKey: VarKey, value: number): string {
  const stops = PALETTES[varKey].stops!;
  if (value <= stops[0][0]) return stops[0][1];
  if (value >= stops[stops.length - 1][0]) return stops[stops.length - 1][1];
  let i = 1;
  while (i < stops.length && stops[i][0] < value) i++;
  const [v0, c0] = stops[i - 1];
  const [v1, c1] = stops[i];
  const t = (value - v0) / (v1 - v0 || 1);
  const mix = (a: number, b: number) => Math.round(a + (b - a) * t);
  const rgb = (h: string) => [parseInt(h.slice(1, 3), 16), parseInt(h.slice(3, 5), 16), parseInt(h.slice(5, 7), 16)];
  const [r0, g0, b0] = rgb(c0);
  const [r1, g1, b1] = rgb(c1);
  return `rgb(${mix(r0, r1)}, ${mix(g0, g1)}, ${mix(b0, b1)})`;
}

export function domain(varKey: VarKey): [number, number] {
  const stops = PALETTES[varKey].stops!;
  return [stops[0][0], stops[stops.length - 1][0]];
}

// The plain-language bands with a representative colour and midpoint for each,
// for the legend scale. Returns [] for variables that have no bands.
export function bandScale(varKey: VarKey): { label: string; color: string }[] {
  const meta = PALETTES[varKey];
  if (!meta.bands) return [];
  const [lo, hi] = domain(varKey);
  let prev = lo;
  return meta.bands.map(([lt, label]) => {
    const upper = Number.isFinite(lt) ? lt : hi;
    const mid = Math.max(lo, Math.min(hi, (prev + upper) / 2));
    prev = upper;
    return { label, color: colorAt(varKey, mid) };
  });
}

// CSS linear-gradient string for the legend bar. A banded variable gets hard-edged,
// EQUAL-WIDTH segments — one per band — so the bar reads as a category key that
// lines up with the word swatches below it, rather than a value-proportional wash
// where one open-ended band (e.g. "Clear" over 10–20 km) would swallow the bar.
// Continuous variables keep the smooth, domain-proportional gradient.
export function gradientCss(varKey: VarKey): string {
  const meta = PALETTES[varKey];
  if (meta.banded && meta.bands) {
    const scale = bandScale(varKey);
    const n = scale.length;
    const parts: string[] = [];
    scale.forEach(({ color }, i) => {
      parts.push(`${color} ${((i / n) * 100).toFixed(1)}%`, `${color} ${(((i + 1) / n) * 100).toFixed(1)}%`);
    });
    return `linear-gradient(90deg, ${parts.join(', ')})`;
  }
  const stops = meta.stops!;
  const lo = stops[0][0];
  const hi = stops[stops.length - 1][0];
  const span = hi - lo || 1;
  const parts = stops.map(([v, c]) => `${c} ${(((v - lo) / span) * 100).toFixed(1)}%`);
  return `linear-gradient(90deg, ${parts.join(', ')})`;
}

// A continuous gradient clipped to [min, max] — the relevant slice of the ramp,
// re-spanned to fill the whole bar. For a continuous variable whose observed range
// is far narrower than its quantization domain (temperature never reaches −40…48
// on one frame), this shows the portion that actually carries values instead of
// wasting most of the bar on colours nothing on the map uses. Endpoints are the
// exact ramp colour at min/max so the clip stays locked to the field.
export function clippedGradientCss(varKey: VarKey, min: number, max: number): string {
  const stops = PALETTES[varKey].stops!;
  const span = max - min || 1;
  const parts: string[] = [`${colorAt(varKey, min)} 0%`];
  for (const [v, c] of stops) {
    if (v > min && v < max) parts.push(`${c} ${(((v - min) / span) * 100).toFixed(1)}%`);
  }
  parts.push(`${colorAt(varKey, max)} 100%`);
  return `linear-gradient(90deg, ${parts.join(', ')})`;
}
